import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { AxiosError } from "axios";
import axiosInstance from "@/lib/axios";

const ExistsResponseSchema = z.object({ exists: z.boolean() });

interface UseCheckFieldExistsOptions
  extends Omit<
    UseQueryOptions<
      boolean,
      AxiosError | z.ZodError,
      boolean,
      ["field-exists-check", string, string]
    >,
    "queryKey" | "queryFn"
  > {
  endpoint: string;
  paramName: string;
  value: string;
  /** Defaults to checking value.trim().length > 0 */
  isValueCheckable?: (value: string) => boolean;
}

export const useCheckFieldExists = ({
  endpoint,
  paramName,
  value,
  isValueCheckable,
  ...options
}: UseCheckFieldExistsOptions) => {
  const checkable = isValueCheckable ?? ((v: string) => v.trim().length > 0);

  return useQuery({
    queryKey: ["field-exists-check", endpoint, value],
    queryFn: async ({ signal }): Promise<boolean> => {
      const response = await axiosInstance.get(endpoint, {
        baseURL: "/api/proxy",
        params: { [paramName]: value },
        signal,
      });

      const parsed = ExistsResponseSchema.parse(response.data);
      return parsed.exists;
    },
    enabled: checkable(value),
    staleTime: 30_000,
    retry: false,
    ...options,
  });
};